'use strict';
import toast from '@components/Common/Toast.js';
import Loader from '@components/Loading/Loader.js';
import UIIcon from '@icons/UIIcon.js';
import { showAutomatedTagsExportDialog } from '@pages/SpatialVision/components/AutomatedTagsExportDialog.js';
import { SpatialVisionState } from '@states/SpatialVision.js';
import { UIButton, UIDiv, UIText } from '@ui';

const hasBox = detection =>
  Number(detection.width) > 0 && Number(detection.height) > 0;

const detectionLabel = detection =>
  detection.abnormalityType ||
  detection.type ||
  detection.status ||
  detection.title ||
  'Detection';

const detectionKey = (detection, index) =>
  String(detection.id || `${detection.coordX}-${detection.coordY}-${index}`);

const detectionMask = detection => {
  if (!detection?.mask || typeof detection.mask !== 'object') return null;
  const { data } = detection.mask;
  const crop = detection.mask.crop || detection.mask.bbox;
  if (!data || !crop) return null;

  const offsetX = Number(crop.offsetX);
  const offsetY = Number(crop.offsetY);
  const width = Number(crop.width);
  const height = Number(crop.height);
  if (
    !Number.isFinite(offsetX) ||
    !Number.isFinite(offsetY) ||
    width <= 0 ||
    height <= 0
  ) {
    return null;
  }

  return {
    data: String(data).startsWith('data:image/')
      ? String(data)
      : `data:image/png;base64,${data}`,
    offsetX,
    offsetY,
    width,
    height,
  };
};

const isChatSession = session =>
  !session.sequenceId && !session.sequence && Boolean(session.prompt);

const conditionResultsForSession = session =>
  (
    session?.conditionResults ||
    session?.resultMetadata?.conditionResults ||
    session?.resultMetadata?.condition_results ||
    []
  ).filter(result => result && typeof result === 'object');

const conditionEvidenceIndexes = result => {
  const indexes =
    result?.relatedDetectionIndexes || result?.related_detection_indexes || [];
  if (!Array.isArray(indexes)) return [];
  return indexes
    .map(index => Number(index))
    .filter(
      (index, position, values) =>
        Number.isInteger(index) &&
        index >= 0 &&
        values.indexOf(index) === position,
    );
};

const formatValue = value =>
  String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const HIDDEN_RESULT_STATUSES = new Set(['matched', 'not matched', 'uncertain']);

const shouldHideResultStatus = status =>
  HIDDEN_RESULT_STATUSES.has(formatValue(status).toLowerCase());

const conditionModeLabel = mode =>
  formatValue(mode || 'text-conversational').replace(/\b\w/g, character =>
    character.toUpperCase(),
  );

export default class SpatialVisionSessionResult {
  constructor({
    showUserMessage = true,
    showAutomatedTagsExport = false,
  } = {}) {
    this.showUserMessage = showUserMessage;
    this.showAutomatedTagsExport = showAutomatedTagsExport;
  }

  render(session) {
    const conversation = new UIDiv().setClass('space-y-4');
    if (!session) {
      conversation.add(
        this.#sessionState('empty', 'No vision result returned'),
      );
      return conversation;
    }

    if (this.showUserMessage)
      conversation.add(this.#renderUserMessage(session));
    conversation.add(this.#renderAssistantMessage(session));
    return conversation;
  }

  #renderUserMessage(session) {
    const promptText = session.prompt || '';
    const bubble = new UIDiv()
      .setClass(
        'max-w-[88%] bg-[var(--o-hive-primary-80)] text-white rounded-lg overflow-hidden shadow-sm',
      )
      .add(
        new UIDiv()
          .setClass('px-4 py-3 space-y-3')
          .add(
            new UIText(
              promptText ||
                (isChatSession(session) ? 'Prompt' : 'Run sequence'),
            ).setClass('text-sm break-words whitespace-pre-wrap block'),
          ),
      );

    if (session.inputImageUrl) {
      const image = document.createElement('img');
      image.src = session.inputImageUrl;
      image.alt = 'Prompt input';
      image.className =
        'w-full max-h-80 object-contain bg-[var(--o-hive-primary-80)] border-t border-[var(--o-hive-primary-80)]';
      bubble.dom.appendChild(image);
    }

    return new UIDiv().setClass('flex justify-end').add(bubble);
  }

  #renderAssistantMessage(session) {
    const isRunning =
      session.status === 'running' || session.status === 'partial';
    const isFailed = session.status === 'failed';
    const detections = session.detections || [];
    const conditionResults = conditionResultsForSession(session);
    const bubble = new UIDiv().setClass(
      'max-w-[94%] bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden',
    );

    const body = new UIDiv().setClass('p-4 space-y-5');
    if (isRunning) {
      body.add(
        this.#renderInputPreview(session, true),
        this.#sessionState('running', 'Vision analysis is running'),
      );
    } else if (isFailed) {
      body.add(
        this.#renderInputPreview(session, false),
        this.#sessionState('failed', 'Vision analysis failed'),
      );
    } else if (session.status === 'canceled') {
      body.add(
        this.#renderInputPreview(session, false),
        this.#sessionState('canceled', 'Vision analysis canceled'),
      );
    } else if (conditionResults.length) {
      body.add(
        this.#renderInspectionReport(session, detections, conditionResults),
      );
    } else if (session.llmOutput || detections.length) {
      if (session.llmOutput) body.add(this.#renderLlmOutput(session.llmOutput));
      this.#groupDetectionsByStep(detections).forEach(group =>
        body.add(this.#renderStepResult(group)),
      );
      if (this.showAutomatedTagsExport && session.canExportAutomatedTags) {
        body.add(this.#renderAutomatedTagsExportAction(session));
      }
    } else {
      body.add(this.#sessionState('empty', 'No LLM output returned'));
    }

    bubble.add(body);
    return new UIDiv().setClass('flex justify-center').add(bubble);
  }

  #renderInputPreview(session, isRunning) {
    if (!session.inputImageUrl) return new UIDiv().setClass('hidden');

    const wrapper = new UIDiv().setClass('relative overflow-hidden rounded-lg');
    const image = document.createElement('img');
    image.src = session.inputImageUrl;
    image.alt = 'Detection input';
    image.className = [
      'h-48 w-full object-contain bg-slate-100 border border-gray-200 rounded-lg',
      isRunning ? 'blur-sm opacity-80' : '',
    ]
      .filter(Boolean)
      .join(' ');
    wrapper.dom.appendChild(image);

    if (isRunning) {
      wrapper.add(
        new UIDiv()
          .setClass(
            'absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/40 text-slate-700',
          )
          .add(
            new Loader('', true),
            new UIButton()
              .setClass(
                'rounded-full border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-600 shadow-sm transition-colors hover:bg-red-50',
              )
              .add(new UIText('Cancel'))
              .onClick(async e => {
                e.stopPropagation();
                if (session.jobId) {
                  if (session.jobItemId) {
                    await SpatialVisionState.cancelJobItem(
                      session.jobId,
                      session.jobItemId,
                    );
                    return;
                  }
                  await SpatialVisionState.cancelJob(session.jobId);
                  return;
                }
                await SpatialVisionState.cancelSession(session.id);
              }),
          ),
      );
    }

    return wrapper;
  }

  #renderAutomatedTagsExportAction(session) {
    return new UIDiv()
      .setClass('flex justify-end border-t border-gray-100 pt-3')
      .add(
        new UIButton()
          .setClass(
            'inline-flex items-center gap-2 rounded-md bg-[var(--o-hive-primary-100)] px-3 py-2 text-xs font-semibold text-white hover:bg-[var(--o-hive-primary-120)]',
          )
          .add(
            new UIIcon('tag', {
              color: 'none',
              strokeColor: 'currentColor',
              size: 14,
            }),
            new UIText('Export Results'),
          )
          .onClick(async event => {
            event.stopPropagation();
            try {
              await showAutomatedTagsExportDialog(
                session.automatedTagsExportItem,
              );
            } catch (error) {
              console.error('Automated tags export failed:', error);
              toast.error('Automated tags export failed');
            }
          }),
      );
  }

  #renderInspectionReport(session, detections, conditionResults) {
    const overlayId = this.#overlayId();
    const imageUrl =
      session.outputImageUrl ||
      detections.find(detection => detection.outputImageUrl)?.outputImageUrl ||
      session.inputImageUrl;
    const report = new UIDiv().setClass('space-y-4');
    report.add(this.#renderInspectionHeader(session, conditionResults));

    const matrix = this.#renderVerificationMatrix(
      conditionResults,
      detections,
      overlayId,
    );
    if (imageUrl) {
      const reportGrid = new UIDiv().setClass('grid gap-4');
      reportGrid.setStyle('gridTemplateColumns', [
        'repeat(auto-fit, minmax(min(100%, 360px), 1fr))',
      ]);
      reportGrid.add(
        matrix,
        new UIDiv()
          .setClass('space-y-2')
          .add(
            new UIDiv()
              .setClass('flex items-center justify-between gap-2')
              .add(
                new UIText('Annotated image').setClass(
                  'text-xs font-semibold uppercase tracking-wide text-slate-500',
                ),
                new UIText(
                  `${detections.filter(hasBox).length} box${
                    detections.filter(hasBox).length === 1 ? '' : 'es'
                  }`,
                ).setClass('text-xs text-slate-400'),
              ),
            this.#imageWithOverlay(
              imageUrl,
              'Annotated inspection image',
              detections,
              overlayId,
            ),
          ),
      );
      report.add(reportGrid);
    } else {
      report.add(matrix);
    }

    if (detections.length) {
      report.add(this.#renderEvidenceBoxes(detections, overlayId));
    }
    if (this.showAutomatedTagsExport && session.canExportAutomatedTags) {
      report.add(this.#renderAutomatedTagsExportAction(session));
    }
    return report;
  }

  #renderInspectionHeader(session, conditionResults) {
    const status =
      session.inspectionStatus || session.resultMetadata?.inspectionStatus;
    const answer = session.llmOutput || session.inspectionRationale || '';
    return new UIDiv()
      .setClass(
        'rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 space-y-2',
      )
      .add(
        new UIDiv()
          .setClass('flex flex-wrap items-start justify-between gap-2')
          .add(
            new UIDiv()
              .setClass('min-w-0')
              .add(
                new UIText(session.name || this.#imageLabel(session)).setClass(
                  'block text-sm font-semibold text-slate-900',
                ),
                new UIText(
                  `${conditionResults.length} check${
                    conditionResults.length === 1 ? '' : 's'
                  }`,
                ).setClass('block text-xs text-slate-500'),
              ),
            status && !shouldHideResultStatus(status)
              ? new UIText(formatValue(status)).setClass(
                  'shrink-0 rounded-full border border-blue-100 bg-white px-2 py-1 text-xs font-semibold text-blue-700',
                )
              : new UIDiv().setClass('hidden'),
          ),
        answer
          ? new UIText(answer).setClass(
              'block text-sm leading-relaxed text-slate-700',
            )
          : new UIDiv().setClass('hidden'),
      );
  }

  #renderVerificationMatrix(conditionResults, detections, overlayId) {
    const matrix = new UIDiv().setClass(
      'rounded-lg border border-slate-200 bg-white overflow-hidden',
    );
    matrix.add(
      new UIDiv()
        .setClass(
          'flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-2.5',
        )
        .add(
          new UIText('Verification Matrix').setClass(
            'text-sm font-semibold text-slate-900',
          ),
          new UIText(`${conditionResults.length} checks`).setClass(
            'text-xs text-slate-500',
          ),
        ),
    );
    const list = new UIDiv().setClass('divide-y divide-slate-100');
    conditionResults.forEach((result, index) => {
      list.add(
        this.#renderConditionResult(result, index, detections, overlayId),
      );
    });
    matrix.add(list);
    return matrix;
  }

  #renderConditionResult(result, index, detections, overlayId) {
    const status = this.#conditionStatusMeta(result.resultStatus);
    const row = new UIDiv().setClass('border-l-4 bg-white px-3 py-2.5');
    row.setStyle('borderLeftColor', [status.borderColor]);
    row.add(
      new UIDiv()
        .setClass('flex flex-wrap items-start justify-between gap-3')
        .add(
          new UIDiv()
            .setClass('min-w-0')
            .add(
              new UIText(
                result.conditionName ||
                  result.condition_name ||
                  result.name ||
                  `Condition ${index + 1}`,
              ).setClass('block text-sm font-semibold text-slate-900'),
              result.stepName
                ? new UIText(result.stepName).setClass(
                    'block truncate text-xs text-slate-500',
                  )
                : new UIDiv().setClass('hidden'),
            ),
        ),
      this.#renderConditionMeta(result),
    );

    const answer = result.answer || (!result.rationale ? '' : result.rationale);
    if (answer) {
      row.add(
        new UIText(answer).setClass(
          'mt-1.5 block text-sm leading-snug text-slate-700',
        ),
      );
    }
    row.add(this.#renderConditionEvidence(result, detections, overlayId));

    if (result.rationale && result.rationale !== result.answer) {
      const detail = new UIText(result.rationale).setClass(
        'hidden mt-2 rounded-md bg-slate-50 px-2 py-1.5 text-xs leading-relaxed text-slate-600',
      );
      let expanded = false;
      const toggle = new UIButton()
        .setClass(
          'mt-2 text-xs font-semibold text-blue-600 hover:text-blue-800',
        )
        .add(new UIText('Show evidence note'))
        .onClick(event => {
          event.stopPropagation();
          expanded = !expanded;
          detail.setClass(
            expanded
              ? 'block mt-2 rounded-md bg-slate-50 px-2 py-1.5 text-xs leading-relaxed text-slate-600'
              : 'hidden mt-2 rounded-md bg-slate-50 px-2 py-1.5 text-xs leading-relaxed text-slate-600',
          );
          toggle.clear();
          toggle.add(
            new UIText(expanded ? 'Hide details' : 'Show evidence note'),
          );
        });
      row.add(toggle, detail);
    }
    return row;
  }

  #renderConditionMeta(result) {
    const meta = new UIDiv().setClass('mt-1.5 flex flex-wrap gap-1.5');
    const statusLabel = result.statusLabel || result.status_label;
    if (statusLabel && !shouldHideResultStatus(statusLabel)) {
      meta.add(
        new UIText(statusLabel).setClass(
          'rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-semibold text-emerald-700',
        ),
      );
    }
    meta.add(
      new UIText(conditionModeLabel(result.mode)).setClass(
        'rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600',
      ),
    );
    return meta;
  }

  #renderConditionEvidence(result, detections, overlayId) {
    const indexes = conditionEvidenceIndexes(result);
    const wrapper = new UIDiv().setClass(
      'mt-2 flex flex-wrap items-center gap-1.5',
    );
    if (!indexes.length) {
      return wrapper.add(
        new UIText('No linked evidence boxes').setClass(
          'text-xs text-slate-400',
        ),
      );
    }
    wrapper.add(new UIText('Evidence').setClass('text-xs text-slate-500'));
    indexes.forEach(index => {
      const detection = detections[index];
      if (!detection) return;
      const key = this.#overlayKeyForDetection(detections, index);
      const label = detectionLabel(detection);
      wrapper.add(
        new UIButton()
          .setClass(
            'rounded border border-sky-100 bg-sky-50 px-1.5 py-0.5 text-xs font-semibold text-sky-700 hover:bg-sky-100',
          )
          .add(new UIText(label))
          .onMouseEnter(() => this.#highlightOverlayBox(overlayId, key))
          .onMouseLeave(() => this.#highlightOverlayBox(overlayId, null))
          .onClick(event => {
            event.stopPropagation();
            this.#focusEvidence(overlayId, key);
          }),
      );
    });
    return wrapper;
  }

  #renderEvidenceBoxes(detections, overlayId) {
    const localized = detections.filter(hasBox);
    if (!localized.length) return new UIDiv().setClass('hidden');
    return new UIDiv()
      .setClass(
        'rounded-lg border border-slate-200 bg-white px-3 py-3 space-y-2',
      )
      .add(
        new UIDiv()
          .setClass('flex items-center justify-between gap-2')
          .add(
            new UIText('Supporting evidence boxes').setClass(
              'text-sm font-semibold text-slate-900',
            ),
            new UIText(`${localized.length} localized`).setClass(
              'text-xs text-slate-500',
            ),
          ),
        this.#renderDetectionPills(localized),
        this.#renderLocalizedDetectionList(localized, overlayId),
      );
  }

  #conditionStatusMeta(status) {
    if (status === 'matched') {
      return {
        borderColor: '#10B981',
      };
    }
    if (status === 'not_matched') {
      return {
        borderColor: '#94A3B8',
      };
    }
    return {
      borderColor: '#F59E0B',
    };
  }

  #imageLabel(session) {
    if (session.batchTotal) {
      return `Image ${Number(session.batchIndex ?? 0) + 1} of ${
        session.batchTotal
      }`;
    }
    return 'Spatial Vision result';
  }

  #overlayKeyForDetection(detections, detectionIndex) {
    const detection = detections[detectionIndex];
    if (!detection) return String(detectionIndex);
    const boxedIndex = detections
      .filter(hasBox)
      .findIndex(item => item === detection);
    return detectionKey(
      detection,
      boxedIndex >= 0 ? boxedIndex : detectionIndex,
    );
  }

  #renderLlmOutput(text) {
    return new UIText(text).setClass(
      'text-sm text-gray-600 whitespace-pre-wrap break-words block leading-relaxed',
    );
  }

  #groupDetectionsByStep(detections) {
    const groups = new Map();
    detections.forEach(detection => {
      const key = detection.stepId || detection.step?.id || 'general';
      if (!groups.has(key)) {
        groups.set(key, {
          title: detection.step?.name || detection.captureName || 'Result',
          detections: [],
        });
      }
      groups.get(key).detections.push(detection);
    });
    return [...groups.values()];
  }

  #renderStepResult(group) {
    const checks = group.detections.filter(
      detection => detection.resultKind === 'check',
    );
    const localized = group.detections.filter(
      detection => detection.resultKind === 'detection' && hasBox(detection),
    );
    const textOnly = group.detections.filter(
      detection => detection.resultKind === 'detection' && !hasBox(detection),
    );
    const body = new UIDiv().setClass('space-y-3');

    if (checks.length) body.add(this.#renderCheckList(checks));
    textOnly.forEach(detection =>
      body.add(this.#renderResultSummary(detection)),
    );
    if (localized.length) body.add(this.#renderLocalizedResults(localized));

    return body;
  }

  #renderResultSummary(detection) {
    return new UIDiv()
      .setClass('border-l-2 border-gray-200 pl-3')
      .add(
        new UIText(detection.title).setClass('font-medium text-sm block'),
        new UIText(detection.description).setClass(
          'text-xs text-gray-600 mt-1 break-words block',
        ),
      );
  }

  #renderCheckList(checks) {
    const list = new UIDiv().setClass('space-y-2');
    checks.forEach(check => {
      const isPass = check.status === 'pass';
      const isFail = check.status === 'fail';
      list.add(
        new UIDiv()
          .setClass('flex items-start gap-3')
          .add(
            this.#statusBadge(isPass ? 'pass' : isFail ? 'fail' : 'pending'),
            new UIDiv()
              .setClass('min-w-0')
              .add(
                new UIText(check.title).setClass('text-sm font-medium block'),
                new UIText(check.description).setClass(
                  'text-xs text-gray-600 break-words block',
                ),
              ),
          ),
      );
    });
    return list;
  }

  #renderLocalizedResults(detections) {
    const outputImageUrl = detections.find(
      detection => detection.outputImageUrl,
    )?.outputImageUrl;
    const total = detections.length;
    const overlayId = this.#overlayId();

    return new UIDiv()
      .setClass('space-y-3')
      .add(
        new UIDiv()
          .setClass('flex items-start gap-3')
          .add(
            this.#statusBadge('pass'),
            new UIDiv()
              .setClass('min-w-0 flex-1')
              .add(
                new UIText(
                  `${total} localized result${total === 1 ? '' : 's'}`,
                ).setClass('font-medium text-sm block'),
              ),
          ),
        this.#imageWithOverlay(
          outputImageUrl,
          'Annotated detections',
          detections,
          overlayId,
        ),
        this.#renderDetectionPills(detections),
        this.#renderLocalizedDetectionList(detections, overlayId),
      );
  }

  #renderDetectionPills(detections) {
    const groups = new Map();
    detections.forEach(detection => {
      const label = detectionLabel(detection);
      const color = detection.colorHex || '#3B82F6';
      if (!groups.has(label)) groups.set(label, { count: 0, color });
      groups.get(label).count += 1;
    });

    const pills = new UIDiv().setClass('flex flex-wrap gap-1.5');
    groups.forEach(({ count, color }, label) => {
      const dot = new UIDiv().setClass('h-1.5 w-1.5 rounded-full');
      dot.setStyle('backgroundColor', [color]);

      const pill = new UIDiv()
        .setClass(
          'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
        )
        .add(
          dot,
          new UIText(label).setClass('break-words'),
          new UIText(`x${count}`).setClass('opacity-60'),
        );
      pill.setStyle('backgroundColor', [`${color}1A`]);
      pill.setStyle('color', [color]);
      pills.add(pill);
    });
    return pills;
  }

  #renderLocalizedDetectionList(detections, overlayId) {
    const details = new UIDiv().setClass('hidden');
    const list = new UIDiv().setClass(
      'mt-2 ml-1 space-y-0.5 border-l-2 border-gray-200 pl-3',
    );
    detections.forEach((detection, index) => {
      const color = detection.colorHex || '#3B82F6';
      const dot = new UIDiv().setClass('h-1.5 w-1.5 rounded-full');
      dot.setStyle('backgroundColor', [color]);

      const row = new UIDiv()
        .setClass(
          'flex items-center justify-between gap-3 rounded px-1 py-1.5 text-xs transition-colors hover:bg-gray-50',
        )
        .add(
          new UIDiv()
            .setClass('min-w-0 flex items-center gap-2')
            .add(
              dot,
              new UIText(
                detection.title ||
                  detection.description ||
                  `Detection ${index + 1}`,
              ).setClass('text-gray-900 block break-words'),
              detection.area
                ? new UIText(detection.area).setClass(
                    'text-gray-500 flex-shrink-0',
                  )
                : new UIDiv().setClass('hidden'),
            ),
          new UIText(
            `${detection.coordX}, ${detection.coordY} ${detection.width} x ${detection.height}`,
          ).setClass(
            'text-[10px] font-mono text-gray-400 tabular-nums flex-shrink-0',
          ),
        );
      const key = detectionKey(detection, index);
      row.dom.dataset.spatialVisionEvidenceOverlay = overlayId || '';
      row.dom.dataset.spatialVisionEvidenceKey = key;
      row.onMouseEnter(() => this.#highlightOverlayBox(overlayId, key));
      row.onMouseLeave(() => this.#highlightOverlayBox(overlayId, null));
      list.add(row);
    });
    details.add(list);

    const toggleText = new UIText(
      `Show ${detections.length} detection${detections.length === 1 ? '' : 's'}`,
    );
    const chevron = this.#icon('dropdown');
    chevron.setClass('transition-transform');
    let expanded = false;
    const button = new UIButton()
      .setClass(
        'flex items-center gap-1.5 text-xs text-gray-500 transition-colors hover:text-gray-900',
      )
      .add(chevron, toggleText)
      .onClick(() => {
        expanded = !expanded;
        details.setClass(expanded ? 'block' : 'hidden');
        chevron.setClass(
          expanded ? 'transition-transform rotate-180' : 'transition-transform',
        );
        toggleText.setValue(
          expanded
            ? 'Hide details'
            : `Show ${detections.length} detection${
                detections.length === 1 ? '' : 's'
              }`,
        );
      });

    return new UIDiv().add(button, details);
  }

  #statusBadge(status) {
    const iconName =
      status === 'pass'
        ? 'check'
        : status === 'fail'
          ? 'circle-cross'
          : 'circle-warning';
    return new UIDiv()
      .setClass(
        `mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${this.#statusBadgeClass(
          status,
        )}`,
      )
      .add(this.#icon(iconName));
  }

  #statusBadgeClass(status) {
    if (status === 'pass') return 'bg-green-50 text-green-600';
    if (status === 'fail') return 'bg-red-50 text-red-600';
    if (status === 'canceled') return 'bg-slate-100 text-slate-500';
    return 'bg-amber-50 text-amber-600';
  }

  #imageWithOverlay(src, title, detections = [], overlayId = null) {
    if (!src) return this.#sessionState('empty', 'No image available');

    const frame = new UIDiv().setClass(
      'group relative w-full max-h-96 overflow-hidden rounded border border-gray-300 bg-black/5 cursor-pointer',
    );
    const img = document.createElement('img');
    img.src = src;
    img.alt = title;
    img.className = 'block w-full max-h-96 object-contain';
    const boxOverlay = this.#createBoxOverlay(img, detections, overlayId);

    const viewButton = new UIButton()
      .setClass(
        'absolute right-3 top-3 flex items-center gap-1.5 rounded bg-black/65 px-2.5 py-1.5 text-xs font-medium text-white opacity-0 transition-opacity group-hover:opacity-100',
      )
      .setTitle('View larger')
      .add(this.#icon('eye-open'), new UIText('View'))
      .onClick(event => {
        event.stopPropagation();
        this.#openImageViewer(src, title, detections);
      });

    frame.onClick(() => this.#openImageViewer(src, title, detections));
    frame.dom.appendChild(img);
    frame.dom.appendChild(boxOverlay);
    frame.add(viewButton);
    return frame;
  }

  #openImageViewer(src, title, detections = []) {
    const overlayId = this.#overlayId();
    const overlay = new UIDiv().setClass(
      'fixed inset-0 z-[9999] flex flex-col bg-black/90 text-white',
    );
    const imageWrap = new UIDiv().setClass(
      'flex-1 overflow-auto p-4 flex items-center justify-center',
    );
    const imageFrame = new UIDiv().setClass(
      'relative max-w-full max-h-full inline-block',
    );
    const img = document.createElement('img');
    img.src = src;
    img.alt = title;
    img.className =
      'max-w-full max-h-full object-contain transition-transform duration-150';
    img.style.transformOrigin = 'center center';
    const boxOverlay = this.#createBoxOverlay(img, detections, overlayId);

    const close = () => {
      document.removeEventListener('keydown', handleKeyDown);
      overlay.dom.remove();
    };

    const handleKeyDown = event => {
      if (event.key === 'Escape') close();
    };

    const toolbar = new UIDiv()
      .setClass(
        'flex items-center justify-end gap-3 border-b border-white/10 px-4 py-3',
      )
      .add(
        new UIDiv()
          .setClass('flex items-center gap-2')
          .add(
            new UIButton()
              .setClass('rounded bg-white/10 p-2 hover:bg-white/20')
              .setTitle('Close')
              .add(this.#icon('circle-cross'))
              .onClick(close),
          ),
      );

    imageFrame.dom.appendChild(img);
    imageFrame.dom.appendChild(boxOverlay);
    imageWrap.add(imageFrame);
    imageWrap.onClick(event => {
      if (event.target === imageWrap.dom) close();
    });

    overlay.add(toolbar, imageWrap);
    document.body.appendChild(overlay.dom);
    document.addEventListener('keydown', handleKeyDown);
  }

  #createBoxOverlay(img, detections = [], overlayId = null) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.classList.add(
      'pointer-events-none',
      'absolute',
      'inset-0',
      'h-full',
      'w-full',
      'overflow-visible',
    );
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    if (overlayId) svg.dataset.overlayId = overlayId;

    const render = () => {
      const imageWidth = img.naturalWidth || 1;
      const imageHeight = img.naturalHeight || 1;
      svg.setAttribute('viewBox', `0 0 ${imageWidth} ${imageHeight}`);
      svg.replaceChildren();

      const defs = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'defs',
      );
      svg.appendChild(defs);

      detections.filter(hasBox).forEach((detection, index) => {
        const color = detection.colorHex || '#00AEEF';
        const key = detectionKey(detection, index);
        const x = Math.max(0, Number(detection.coordX) || 0);
        const y = Math.max(0, Number(detection.coordY) || 0);
        const width = Math.min(
          imageWidth - x,
          Math.max(0, Number(detection.width) || 0),
        );
        const height = Math.min(
          imageHeight - y,
          Math.max(0, Number(detection.height) || 0),
        );
        if (width <= 0 || height <= 0) return;

        const mask = detectionMask(detection);
        if (mask) {
          const maskId = `spatial-vision-mask-${overlayId || 'overlay'}-${index}`;
          const svgMask = document.createElementNS(
            'http://www.w3.org/2000/svg',
            'mask',
          );
          svgMask.setAttribute('id', maskId);
          svgMask.setAttribute('maskUnits', 'userSpaceOnUse');
          svgMask.setAttribute('x', mask.offsetX);
          svgMask.setAttribute('y', mask.offsetY);
          svgMask.setAttribute('width', mask.width);
          svgMask.setAttribute('height', mask.height);
          svgMask.setAttribute('maskContentUnits', 'userSpaceOnUse');
          svgMask.setAttribute('mask-type', 'luminance');
          svgMask.style.maskType = 'luminance';

          const maskImage = document.createElementNS(
            'http://www.w3.org/2000/svg',
            'image',
          );
          maskImage.setAttribute('href', mask.data);
          maskImage.setAttributeNS(
            'http://www.w3.org/1999/xlink',
            'href',
            mask.data,
          );
          maskImage.setAttribute('x', mask.offsetX);
          maskImage.setAttribute('y', mask.offsetY);
          maskImage.setAttribute('width', mask.width);
          maskImage.setAttribute('height', mask.height);
          maskImage.setAttribute('preserveAspectRatio', 'none');
          svgMask.appendChild(maskImage);
          defs.appendChild(svgMask);

          const maskRect = document.createElementNS(
            'http://www.w3.org/2000/svg',
            'rect',
          );
          maskRect.setAttribute('x', mask.offsetX);
          maskRect.setAttribute('y', mask.offsetY);
          maskRect.setAttribute('width', mask.width);
          maskRect.setAttribute('height', mask.height);
          maskRect.setAttribute('fill', color);
          maskRect.setAttribute('opacity', '0.62');
          maskRect.setAttribute('mask', `url(#${maskId})`);
          maskRect.dataset.detectionKey = key;
          maskRect.dataset.overlayItem = 'true';
          maskRect.dataset.overlayKind = 'mask';
          maskRect.dataset.opacity = '0.62';
          maskRect.dataset.highlightOpacity = '0.86';
          svg.appendChild(maskRect);
        }

        const rect = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'rect',
        );
        rect.setAttribute('x', x);
        rect.setAttribute('y', y);
        rect.setAttribute('width', width);
        rect.setAttribute('height', height);
        rect.setAttribute('fill', 'none');
        rect.setAttribute('stroke', color);
        rect.setAttribute('stroke-width', Math.max(1.5, imageWidth * 0.0015));
        rect.setAttribute('opacity', '0.72');
        rect.setAttribute('vector-effect', 'non-scaling-stroke');
        rect.dataset.detectionKey = key;
        rect.dataset.overlayItem = 'true';
        rect.dataset.overlayKind = 'box';
        rect.dataset.strokeWidth = String(Math.max(1.5, imageWidth * 0.0015));
        rect.dataset.highlightStrokeWidth = String(
          Math.max(3, imageWidth * 0.003),
        );

        svg.appendChild(rect);
      });
    };

    if (img.complete && img.naturalWidth) render();
    else img.addEventListener('load', render, { once: true });

    return svg;
  }

  #overlayId() {
    return `spatial-vision-overlay-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2)}`;
  }

  #highlightOverlayBox(overlayId, detectionKeyValue) {
    if (!overlayId) return;
    document
      .querySelectorAll(
        `svg[data-overlay-id="${overlayId}"] [data-overlay-item="true"]`,
      )
      .forEach(item => {
        const active =
          detectionKeyValue &&
          item.dataset.detectionKey === String(detectionKeyValue);
        if (item.dataset.overlayKind === 'mask') {
          item.setAttribute(
            'opacity',
            active
              ? item.dataset.highlightOpacity || '0.62'
              : item.dataset.opacity || '0.34',
          );
          return;
        }

        item.setAttribute(
          'stroke-width',
          active
            ? item.dataset.highlightStrokeWidth || '3'
            : item.dataset.strokeWidth || '1.5',
        );
        item.setAttribute('opacity', active ? '1' : '0.72');
        item.setAttribute('filter', active ? 'drop-shadow(0 0 5px white)' : '');
      });
  }

  #focusEvidence(overlayId, detectionKeyValue) {
    this.#highlightOverlayBox(overlayId, detectionKeyValue);
    const row = [
      ...document.querySelectorAll('[data-spatial-vision-evidence-key]'),
    ].find(
      item =>
        item.dataset.spatialVisionEvidenceOverlay === overlayId &&
        item.dataset.spatialVisionEvidenceKey === String(detectionKeyValue),
    );
    if (!row) return;
    row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    row.classList.add('ring-2', 'ring-sky-300', 'bg-sky-50');
    window.setTimeout(() => {
      row.classList.remove('ring-2', 'ring-sky-300', 'bg-sky-50');
      this.#highlightOverlayBox(overlayId, null);
    }, 1200);
  }

  #sessionState(type, text) {
    const classes = {
      running: 'bg-blue-50 border-blue-200 text-blue-700',
      failed: 'bg-red-50 border-red-200 text-red-700',
      canceled: 'bg-slate-50 border-slate-200 text-slate-600',
      empty: 'bg-gray-50 border-gray-200 text-gray-500',
    };

    return new UIDiv()
      .setClass(
        `rounded border px-3 py-4 text-sm flex items-center gap-2 ${classes[type]}`,
      )
      .add(
        type === 'running'
          ? new UIDiv().setClass(
              'w-3 h-3 rounded-full border-2 border-blue-300 border-t-blue-600 animate-spin flex-shrink-0',
            )
          : this.#icon(
              type === 'failed' ? 'circle-cross' : 'circle-info-message',
            ),
        new UIText(text).setClass('break-words'),
      );
  }

  #icon(name, options = {}) {
    return new UIIcon(name, {
      color: 'none',
      strokeColor: 'currentColor',
      size: 16,
      ...options,
    });
  }
}
